<template>
  <div @click="stopPropagation">
    <div ref="toggleRef" @click="toggleDropdown">
      <slot name="dropDownHeader"></slot>
    </div>
    <div :class="$style.dropdownContainer" :style="{ width: getWidth() }">
      <transition :name="transition">
        <div
          v-if="isOpen"
          v-on-click-outside="onClickOutside"
          :class="[$style.expandableContainer, $style[transitionMap[transition]]]"
        >
          <div>
            <slot name="dropdown-content"></slot>
          </div>
          <div v-if="displayActionButtons" :class="$style.dropdownFooterContainer">
            <Button v-if="props.displayClearButton" :label="secondaryButtonLabel" outlined @click="onClearClick" />
            <Button label="Save" :disabled="props.disableSaveButton" @click="onConfirmClick" />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { vOnClickOutside } from "@vueuse/components";
import Button from "primevue/button";
import { ref, watch } from "vue";

const toggleRef = ref(null);
const getWidth = () => `${toggleRef.value?.offsetWidth}px`;
enum TransitionTypes {
  SCALE_FADE_CENTER = "scale-fade-center",
  SCALE_FADE_LEFT = "scale-fade-left",
  SCALE_FADE_RIGHT = "scale-fade-right",
}

const transitionMap = {
  [TransitionTypes.SCALE_FADE_CENTER]: "scaleFadeCenter",
  [TransitionTypes.SCALE_FADE_LEFT]: "scaleFadeLeft",
  [TransitionTypes.SCALE_FADE_RIGHT]: "scaleFadeRight",
};
const props = defineProps({
  disableSaveButton: {
    type: Boolean,
    default: false,
  },
  displayClearButton: {
    type: Boolean,
    default: true,
  },
  displayActionButtons: {
    type: Boolean,
    default: true,
  },
  closeOnSave: {
    type: Boolean,
    default: true,
  },
  isEnabled: {
    type: Boolean,
    default: true,
  },
  transition: {
    type: String,
    required: false,
    default: "scale-fade-center",
  },
  shouldCloseOnOutsideClick: {
    type: Boolean,
    default: true,
  },
  shouldClose: {
    type: Boolean,
    default: false,
  },
  secondaryButtonLabel: {
    type: String,
    default: "Clear",
  },
  shouldStopPropagation: {
    type: Boolean,
    default: false,
  },
});

const isOpen = ref(false);

const toggleDropdown = (event) => {
  event.stopPropagation();
  if (props.isEnabled) {
    isOpen.value = !isOpen.value;
  }
};

const emit = defineEmits(["confirm", "clear", "onClickOutside", "close"]);

const handleClose = () => {
  isOpen.value = false;
  emit("close");
};

const onConfirmClick = (event) => {
  event.stopPropagation();
  emit("confirm");
  if (props.closeOnSave) {
    handleClose();
  }
};

const onClearClick = (event) => {
  event.stopPropagation();
  emit("clear");
  handleClose();
};

const onClickOutside = () => {
  if (!props.shouldCloseOnOutsideClick) {
    return;
  }

  handleClose();
  emit("onClickOutside");
};

watch(
  () => props.shouldClose,
  (newValue) => {
    if (newValue) {
      handleClose();
    }
  }
);

const stopPropagation = (event) => {
  if (props.shouldStopPropagation) {
    event.stopPropagation();
  }
};
</script>
<style module>
.dropdownContainer {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 100%;
}

.expandableContainer {
  background-color: #ffffff;
  padding: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  z-index: 2;
}

.scaleFadeCenter {
  transform: translate(-50%, -50%);
  transition: transform 0.3s ease-in-out;
}
.scaleFadeRight {
  transform: translate(-20%, 0%);
  transition: transform 0.3s ease-in-out;
}
.scaleFadeLeft {
  transform: translate(-80%, 0%);
  transition: transform 0.3s ease-in-out;
}
.dropdownFooterContainer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>

<style scoped>
.scale-fade-center-enter-active,
.scale-fade-center-leave-active {
  transition: opacity 0.5s, transform 0.3s cubic-bezier(0.86, 0, 0.07, 1);
}
.scale-fade-center-enter-from,
.scale-fade-center-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.5);
}
.scale-fade-center-enter-to,
.scale-fade-center-leave-from {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/*
  * Scale Fade Left
 */
.scale-fade-left-enter-active,
.scale-fade-left-leave-active {
  transition: opacity 0.5s, transform 0.3s cubic-bezier(0.86, 0, 0.07, 1);
}
.scale-fade-left-enter-from,
.scale-fade-left-leave-to {
  opacity: 0;
  transform: translate(-80%, 0%) scale(0.5);
}
.scale-fade-left-enter-to,
.scale-fade-left-leave-from {
  opacity: 1;
  transform: translate(-80%, 0%) scale(1);
}
/*
  * Scale Fade Right
 */
.scale-fade-right-enter-active,
.scale-fade-right-leave-active {
  transition: opacity 0.5s, transform 0.3s cubic-bezier(0.86, 0, 0.07, 1);
}
.scale-fade-right-enter-from,
.scale-fade-right-leave-to {
  opacity: 0;
  transform: translate(-20%, 0%) scale(0.5);
}
.scale-fade-right-enter-to,
.scale-fade-right-leave-from {
  opacity: 1;
  transform: translate(-20%, 0%) scale(1);
}
</style>
